import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import classnames from 'classnames';
import { ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { useMediaQuery } from '../../theme/useMediaQuery';

type formatterType = 'currency' | 'number';

interface BarChartProps {
  data: BarChartData[];
  className?: string;
  title: string;
  subtitle?: string;
  type?: formatterType;
}
type BarChartData = {
  name: string;
  total: number | string;
  value: number;
};

const BarChart = ({
  data,
  className,
  title,
  subtitle,
  type
}: BarChartProps) => {
  const isMobile = useMediaQuery('(max-width: 767px)');

  return (
  <div className={classnames('rounded-lg border border-[#EBEBEB]', className)}>
    <p className="pl-7 pr-7 pt-4 pb-2 text-[#575757] text-sm font-bold">
      {title}
    </p>
    <p className="pl-7 pr-7 pb-2 text-[#575757] text-2xl font-black uppercase min-h-[35px]">
      {subtitle}
    </p>
    <ResponsiveContainer width="100%" height={300}>
      <RechartsBarChart
        data={data}
        margin={{
          top: 20,
          right: isMobile ? 15 : 30,
          left: isMobile ? -10 : 10,
          bottom: 20
        }}
        barSize={isMobile ? (data.length > 4 ? 8 : 20) : (data.length > 6 ? 10 : 30)}
      >
        <XAxis
          dataKey="name"
          axisLine={false}
          tickLine={false}
          padding={{ left: 10, right: 0 }}
          tick={<CustomTick type={type} />}
          style={{ transform: 'translateY(-17px)' }}
          // this prevents the x axis from being cut off if the text is too long
          interval={isMobile && data.length > 6 ? 'preserveStartEnd' : 0}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          padding={{ top: 0, bottom: 0 }}
          tick={<CustomTick type={type} />}
          style={{ transform: 'translateY(-17px)' }}
          width={isMobile ? 35 : 60}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: '#fff',
            borderRadius: '10px',
            border: '1px solid #EBEBEB'
          }}
          itemStyle={{ color: '#575757' }}
          cursor={{ fill: 'transparent' }}
          formatter={(value) => formatCurrency(value, type)}
        />

        <Bar
          dataKey="value"
          stackId="a"
          fill="#6585ff"
          background={{ fill: '#eff2ff', radius: 20 }}
          radius={[20, 20, 20, 20]}
        />
      </RechartsBarChart>
    </ResponsiveContainer>
  </div>
  );
};
const CustomTick = (props) => {
  const { x, y, payload, type } = props;
  const isMobile = useMediaQuery('(max-width: 767px)');
  const value = String(payload.value); // Ensure the value is a string

  return (
    <text
      x={x}
      y={y}
      dy={16}
      textAnchor="middle"
      className="text-center text-[10px] font-normal"
      style={{ fill: '#838383' }}
    >
      <tspan x={x} dy={0}>
        {type === 'number' || isNaN(Number(payload.value))
          ? formatText(value)
          : formatNumber(Number(payload.value), isMobile)}
      </tspan>
    </text>
  );
};
export default BarChart;

export const generateEmptyPlaceHolder = (
  name: string,
  value: number,
  total: number
) => ({
  name,
  value,
  total
});

export const formatCurrency = (value: ValueType, type?: formatterType) => {
  if (typeof value === 'number' && type === 'currency') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  }
  // Return the value as is if it's not a currency
  return value;
};

export const formatNumber = (value: number, isMobile?: boolean) => {
  if (value >= 1000000000) {
    return `$${(value / 1000000000).toFixed(0)}B`;
  }
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(0)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  // On mobile, truncate .00 decimals for whole numbers
  if (isMobile && value % 1 === 0) {
    return `$${value.toFixed(0)}`;
  }
  return `$${value.toFixed(2)}`;
};

export const formatText = (value: string) =>
  value.length > 10 ? `${value.substring(0, 10)}...` : value;
